/*
 * Stitch trace (Christmas page POC, owner 2 Nov 2026): every piece in a collection grid starts with a name stitched on its
 * photo; one red thread then relays through the cards and changes each name. On a card it comes in from the left edge while
 * the old name fades, stitches the next name where the embroidery sits and leaves by the right edge; the next card's thread
 * comes in at the height it left, so the line seems to run from card to card. 2.5 seconds a card; after the last it starts
 * again from the first. It runs only while a card is on screen and the tab is visible.
 *
 * The names are the product's own example names (the product metafield folded_label.example_names, in order: the first is the
 * one the card starts with). Everything else comes from the app's published setup for the card's variant (the one the product
 * page uses): the default Layout's text spot (with its tilt) and its default font; a name the spot's rules or font cannot take
 * is left out. A card whose photo is not that setup's image, or whose product has no text spot or no example names, is
 * skipped; so are Design cards. A card with one name keeps it, without the thread. Reduced motion: the first names, stitched,
 * no thread. Geometry: stitch-geometry.js.
 */
import { createFontSession, defaultState, fontIssue, formatForVariant, readPublicRelease, textProblem } from 'folded-label/personalization-contract';
import { iconRowBoxes } from 'folded-label/layout-cards';
import { centreLine, fitWords, pathData, polylineLength } from './stitch-geometry.js';

const LEAD_MS = 400; // thread in (the old name fades meanwhile), and thread out
const STITCH_MS = 1700; // the new name itself; with the leads, 2.5 seconds a card (owner, 2 Nov 2026)
const PULL_MS = 500; // the loose ends pull through while the next card starts
const RASTER = 96; // px font size the centre line is found at
const SVG = 'http://www.w3.org/2000/svg';
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const clamp = (value, low, high) => Math.min(Math.max(value, low), high);
const tick = () => new Promise(resolve => window.requestAnimationFrame(resolve));
const node = (name, attributes = {}, parent) => {
  const element = document.createElementNS(SVG, name);
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, String(value));
  parent?.append(element);
  return element;
};
let unique = 0;

/** Where and how one card stitches, from its variant's published setup and the product's example names; null when it cannot. */
export function stitchPlan(payload, productId, variantId, names) {
  let release;
  try { ({ release } = readPublicRelease(payload, { id: productId, variants: [{ id: variantId }] }, { id: variantId })); } catch { return null; }
  const variant = `gid://shopify/ProductVariant/${variantId}`, preview = release.previewByVariant[variant];
  const published = release.formats.find(format => format.id === release.defaultFormatId);
  if (!preview || !published) return null;
  const format = formatForVariant(release, variant, published), state = defaultState(format, release, variant);
  const texts = format.slots.filter(slot => slot.kind === 'text');
  const slot = texts.find(item => item.required) || texts[0];
  const box = slot && iconRowBoxes(preview, format, state).find(item => item.slotId === slot.id);
  if (!box || !(box.width > 0 && box.height > 0)) return null;
  const style = slot.styles.find(item => item.id === slot.defaults.styleId);
  // Only names the shopper could have typed into this spot: one line, within its rules.
  const fitting = (Array.isArray(names) ? names : []).filter(name => typeof name === 'string' && name.trim() && !name.includes('\n')
    && [...name].length <= style.maxCharactersPerLine && !textProblem(style, name));
  const font = release.references.fonts.find(item => item.id === slot.defaults.fontId);
  if (!fitting.length || !font) return null;
  return { names: fitting, font, preview, spot: { x: box.x, y: box.y, width: box.width, height: box.height, angle: box.angleDeg || 0 } };
}

/** The words' ink at the raster size, and their centre line in raster pixels. */
function rasterise(text, family) {
  const probe = document.createElement('canvas').getContext('2d');
  probe.font = `${RASTER}px ${family}`;
  const metrics = probe.measureText(text);
  const ink = { left: metrics.actualBoundingBoxLeft, right: metrics.actualBoundingBoxRight, ascent: metrics.actualBoundingBoxAscent, descent: metrics.actualBoundingBoxDescent };
  const pad = 4, width = Math.ceil(ink.left + ink.right) + pad * 2, height = Math.ceil(ink.ascent + ink.descent) + pad * 2;
  if (!(width > pad * 2 && height > pad * 2)) return null;
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  context.font = probe.font; context.fillStyle = '#000';
  const penX = pad + ink.left, penY = pad + ink.ascent;
  context.fillText(text, penX, penY);
  const pixels = context.getImageData(0, 0, width, height).data, mask = new Uint8Array(width * height);
  for (let i = 0; i < mask.length; i++) mask[i] = pixels[i * 4 + 3] > 127 ? 1 : 0;
  return { ink, penX, penY, ...centreLine(mask, width, height) };
}

/**
 * The card's overlay: one layer per name (the name, revealed through a mask along its centre line, and that centre line), and
 * the thread leads they share. No needle (owner, 2 Nov 2026): the thread alone draws the name.
 */
function overlay(plan, media) {
  const { preview, spot, names, font } = plan, width = preview.width, height = preview.height;
  const box = { x: spot.x / 100 * width, y: spot.y / 100 * height, width: spot.width / 100 * width, height: spot.height / 100 * height, angle: spot.angle };
  const id = `StitchName-${++unique}`;
  const svg = node('svg', { class: 'stitch-trace', 'data-stitch-trace': '', viewBox: `0 0 ${width} ${height}`, preserveAspectRatio: 'xMidYMid meet', 'aria-hidden': 'true', focusable: 'false' });
  const defs = node('defs', {}, svg);
  // Satin: fine slanted bars over a finished name, sized for the card (about 3 screen px at the card's scale).
  const unit = width / Math.max(media.clientWidth, 1), bar = (6.8 * unit / 2).toFixed(2);
  const satin = node('pattern', { id: `${id}-satin`, patternUnits: 'userSpaceOnUse', width: bar, height: bar, patternTransform: `rotate(${(-62 - spot.angle).toFixed(1)})` }, defs);
  node('rect', { class: 'stitch-trace__satin-line', width: (2.3 * unit / 2).toFixed(2), height: bar }, satin);
  let layers = 0;
  names.forEach(name => {
    const raster = rasterise(name, font.family);
    if (!raster?.runs.length) return;
    const fit = fitWords(raster.ink, box);
    const runs = raster.runs.map(points => points.map(point => fit.place(point, raster.penX, raster.penY)));
    const maskId = `${id}-${layers}`;
    const mask = node('mask', { id: maskId, maskUnits: 'userSpaceOnUse', x: 0, y: 0, width, height }, defs);
    let start = 0;
    const stitch = [];
    runs.forEach((points, i) => {
      if (i) start += Math.hypot(points[0][0] - runs[i - 1].at(-1)[0], points[0][1] - runs[i - 1].at(-1)[1]);
      node('path', { class: 'stitch-trace__reveal stitch-trace__reveal--show', 'data-part': 'reveal', 'data-start': start.toFixed(2), d: pathData(points),
        'stroke-width': (raster.stroke * fit.scale * 1.7 + unit).toFixed(2) }, mask);
      start += polylineLength(points);
      stitch.push(...points);
    });
    const layer = node('g', { class: 'stitch-trace__layer', 'data-layer': layers, 'data-name': name }, svg);
    const ink = node('g', { 'data-name-ink': '', 'data-mask': `url(#${maskId})`, 'data-mask-id': maskId }, layer);
    const words = { x: fit.originX.toFixed(2), y: fit.originY.toFixed(2), 'font-size': (RASTER * fit.scale).toFixed(2), transform: `rotate(${fit.rotate} ${box.x.toFixed(2)} ${box.y.toFixed(2)})` };
    for (const [className, fill] of [['stitch-trace__name', null], ['stitch-trace__satin', `url(#${id}-satin)`]]) {
      const element = node('text', { class: className, ...words, ...(fill ? { fill } : {}) }, ink);
      element.style.fontFamily = font.family; element.textContent = name;
    }
    node('path', { class: 'stitch-trace__guide', 'data-part': 'stitch', d: pathData(stitch) }, layer);
    layers++;
  });
  if (!layers) return null;
  node('path', { class: 'stitch-trace__thread', 'data-part': 'in' }, svg);
  node('path', { class: 'stitch-trace__thread', 'data-part': 'out' }, svg);
  svg.style.setProperty('--stitch-fade', `${LEAD_MS}ms`);
  return svg;
}

/** One card: its first name shown stitched; each run fades the shown name and stitches the next one in its place. */
function card(svg) {
  const leads = { in: svg.querySelector('[data-part="in"]'), out: svg.querySelector('[data-part="out"]') };
  const well = svg.closest('.product-card-media') || svg.parentElement;
  const hide = (path, length) => { path.style.strokeDasharray = `${length} ${length}`; path.style.strokeDashoffset = length; };
  const layers = [...svg.querySelectorAll('[data-layer]')].map(layer => {
    const ink = layer.querySelector('[data-name-ink]'), guide = layer.querySelector('[data-part="stitch"]');
    const pieces = [...svg.querySelectorAll(`#${ink.dataset.maskId} [data-part="reveal"]`)].map(path => ({ path, start: Number(path.dataset.start), length: path.getTotalLength() }));
    return { layer, ink, guide, pieces, length: guide.getTotalLength() };
  });
  let current = 0;
  const stitched = item => {
    item.ink.removeAttribute('mask');
    for (const piece of item.pieces) piece.path.style.strokeDasharray = piece.path.style.strokeDashoffset = '';
    item.layer.classList.add('is-shown', 'is-stitched');
  };
  const unstitched = item => {
    item.layer.classList.remove('is-stitched');
    item.ink.setAttribute('mask', item.ink.dataset.mask);
    for (const piece of item.pieces) hide(piece.path, piece.length);
    item.layer.classList.add('is-shown');
  };
  const settle = () => {
    for (const key of ['in', 'out']) leads[key].style.visibility = 'hidden';
    layers.forEach((item, i) => { if (i !== current) item.layer.classList.remove('is-shown', 'is-stitched'); });
    stitched(layers[current]);
  };
  settle();
  // Screen point -> this svg's user space (follows layout, hover zoom, phone sizes).
  const toUser = (x, y) => new window.DOMPoint(x, y).matrixTransform(svg.getScreenCTM().inverse());
  const curve = (a, b) => `M${a.x} ${a.y}C${a.x + (b.x - a.x) * 0.55} ${a.y} ${b.x - (b.x - a.x) * 0.3} ${b.y} ${b.x} ${b.y}`;
  // The loose ends run out along the lead-in, then the lead-out, then the card settles on its new name.
  async function pull(length) {
    const both = length.in + length.out, begun = window.performance.now();
    for (let done = 0; done < 1;) {
      await tick();
      done = Math.min((window.performance.now() - begun) / PULL_MS, 1);
      const pulled = both * done;
      leads.in.style.strokeDashoffset = -Math.min(pulled, length.in);
      leads.out.style.strokeDashoffset = -clamp(pulled - length.in, 0, length.out);
    }
    settle();
  }
  /* Change this card's name: enter at `entry` (0–1 down the well); resolves with where the thread left as the lead-out reaches the edge. */
  async function run(entry) {
    if (!svg.isConnected || layers.length < 2) return entry; // a colour swatch swapped the photo, or one name only: the thread passes
    const from = layers[current], next = layers[(current + 1) % layers.length];
    const box = well.getBoundingClientRect();
    const first = next.guide.getPointAtLength(0), last = next.guide.getPointAtLength(next.length);
    const lastScreen = new window.DOMPoint(last.x, last.y).matrixTransform(svg.getScreenCTM());
    const exit = clamp((lastScreen.y - box.top) / box.height, 0.2, 0.8);
    leads.in.setAttribute('d', curve(toUser(box.left - 2, box.top + entry * box.height), first));
    leads.out.setAttribute('d', curve(last, toUser(box.right + 2, box.top + exit * box.height)));
    const length = { in: leads.in.getTotalLength(), out: leads.out.getTotalLength() };
    for (const key of ['in', 'out']) { leads[key].style.visibility = ''; hide(leads[key], length[key]); }
    from.layer.classList.remove('is-shown'); // the old name fades while the thread comes in (--stitch-fade)
    for (const [key, ms] of [['in', LEAD_MS], ['stitch', STITCH_MS], ['out', LEAD_MS]]) {
      if (key === 'stitch') { from.layer.classList.remove('is-stitched'); unstitched(next); }
      const total = key === 'stitch' ? next.length : length[key], begun = window.performance.now();
      for (let done = 0; done < 1;) {
        await tick();
        done = Math.min((window.performance.now() - begun) / ms, 1);
        if (key === 'stitch') for (const piece of next.pieces) piece.path.style.strokeDashoffset = piece.length - clamp(total * done - piece.start, 0, piece.length);
        else leads[key].style.strokeDashoffset = total * (1 - done);
      }
    }
    current = layers.indexOf(next);
    stitched(next);
    pull(length);
    return exit;
  }
  return { run, svg, changes: layers.length > 1 };
}

async function prepare(article, proxy) {
  const productId = Number(article.dataset.flProductId), variantId = Number(article.dataset.flItemId?.split('/').at(-1));
  let names;
  try { names = JSON.parse(article.dataset.flExampleNames || '[]'); } catch { return null; }
  const media = article.querySelector('.product-card-media'), image = media?.querySelector('img');
  if (!productId || !variantId || !image || !names.length) return null;
  const response = await fetch(`${proxy}/config?schemaVersion=2&productId=${encodeURIComponent(`gid://shopify/Product/${productId}`)}&variantId=${encodeURIComponent(`gid://shopify/ProductVariant/${variantId}`)}`);
  if (!response.ok) return null;
  const plan = stitchPlan(await response.json(), productId, variantId, names);
  if (!plan) return null;
  // The spot is placed on the setup's image: the card must show that image (same proportions), whole (object-fit: contain).
  // The image tag's own width and height give its proportions, so a lazy photo is not loaded early (or waited for).
  const tagRatio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
  const ratio = tagRatio > 0 && Number.isFinite(tagRatio) ? tagRatio : image.naturalWidth / image.naturalHeight;
  const published = plan.preview.width / plan.preview.height;
  if (!(ratio > 0) || Math.abs(ratio - published) > 0.01 * published) return null;
  const session = createFontSession(() => {});
  try {
    session.sync([plan.font]);
    for (const begun = window.performance.now(); session.state(plan.font) === 'loading' && window.performance.now() - begun < 8000;) await new Promise(resolve => setTimeout(resolve, 50));
    // A name the font cannot draw whole is left out; the card starts with the first name it can.
    const drawable = plan.names.filter(name => !fontIssue(plan.font, name, session));
    if (!drawable.length) return null;
    await document.fonts.load(`${RASTER}px ${plan.font.family}`, drawable.join(' ')).catch(() => {});
    const svg = overlay({ ...plan, names: drawable }, media);
    if (!svg) return null;
    media.append(svg);
    return card(svg);
  } finally {
    if (session.state(plan.font) !== 'ready') session.dispose();
  }
}

async function start(grid) {
  const routes = JSON.parse(document.getElementById('fl-routes')?.textContent || '{}');
  const proxy = `${(routes.root || '/').replace(/\/?$/, '/')}${(grid.dataset.stitchTrace || 'apps/folded-label').replace(/^\/+|\/+$/g, '')}`;
  const articles = [...grid.querySelectorAll('.product-card[data-fl-product-id][data-fl-example-names]')];
  const cards = (await Promise.all(articles.map(article => prepare(article, proxy).catch(() => null)))).filter(Boolean);
  const changing = cards.filter(item => item.changes);
  if (reduced || !changing.length) return; // the first names stay, stitched
  const onScreen = new Set();
  let wake = null;
  const active = () => onScreen.size > 0 && !document.hidden;
  const whenActive = () => active() ? Promise.resolve() : new Promise(resolve => { wake = resolve; });
  const nudge = () => { if (wake && active()) { wake(); wake = null; } };
  const observer = new window.IntersectionObserver(entries => {
    for (const entry of entries) { if (entry.isIntersecting) onScreen.add(entry.target); else onScreen.delete(entry.target); }
    nudge();
  }, { threshold: 0.25 });
  changing.forEach(item => observer.observe(item.svg.closest('.product-card-media') || item.svg));
  document.addEventListener('visibilitychange', nudge);
  let entry = 0.5;
  for (;;) {
    const live = changing.filter(item => item.svg.isConnected);
    if (!live.length) return; // every photo was swapped by a colour swatch: nothing left to stitch
    for (const item of live) { await whenActive(); entry = await item.run(entry); }
  }
}

document.querySelectorAll('[data-stitch-trace]:not(svg)').forEach(grid => { start(grid); });
